<template>
  <GlobalBackgroundWrapper page-key="reservation" settings-position="top-left">
    <div class="reservation-container">
      <!-- 页面头部 -->
      <div class="reservation-header">
        <div class="header-content">
          <i class="el-icon-date header-icon"></i>
          <div class="header-text">
            <h2 class="page-title">预约练车</h2>
            <p class="page-subtitle">选择教练、日期和时段进行练车预约</p>
          </div>
        </div>
      </div>

      <!-- 警告横幅 -->
      <div v-if="isOverReservationLimit" class="reservation-warning-banner">
        <div class="warning-banner-content">
          <i class="el-icon-warning warning-banner-icon"></i>
          <div class="warning-banner-text">
            <div class="warning-title">今日预约次数已用完</div>
            <div class="warning-desc">
              您今日已预约 {{ todayReservationCount }} 次，已达到最大限制 {{ maxDailyAppointments }} 次。
              请选择其他日期进行预约。
            </div>
          </div>
        </div>
      </div>

      <div class="reservation-content">
        <!-- 移动端：教练排班信息放在前面 -->
        <div v-if="isMobile && coachScheduleInfo" class="coach-schedule-section mobile-first">
          <div class="coach-schedule-header">
            <div class="schedule-header-content">
              <i class="el-icon-schedule header-icon"></i>
              <span class="header-text">教练排班信息</span>
              <!-- 添加折叠状态和备注提示 -->
              <div v-if="coachScheduleInfo.remark && !isScheduleExpanded" class="remark-tip">
                <i class="el-icon-chat-dot-round tip-icon"></i>
                <span>教练有备注</span>
              </div>
            </div>
          </div>
          <CoachScheduleInfo
              :coach-schedule-info="coachScheduleInfo"
              :is-mobile="isMobile"
              :expanded="isScheduleExpanded"
              @toggle="toggleScheduleExpanded"
          />
        </div>

        <!-- 主表单区域 -->
        <div class="main-form-section">
          <el-card class="form-card" shadow="hover">
            <div slot="header" class="card-header">
              <div class="header-content">
                <i class="el-icon-tickets header-icon"></i>
                <span class="header-text">预约信息</span>
              </div>
            </div>

            <!-- 加载状态骨架屏 -->
            <div v-if="loading && !timeSlotsLoading" class="form-skeleton">
              <el-skeleton :rows="5" animated />
            </div>

            <div v-else class="form-content">
              <el-form :model="reservationForm" ref="form" class="reservation-form">
                <!-- 教练选择 -->
                <div class="form-group">
                  <div class="form-label">选择教练</div>
                  <el-select
                      v-model="reservationForm.coachId"
                      placeholder="请选择教练"
                      @change="handleCoachChange"
                      clearable
                      class="mobile-select"
                      popper-class="mobile-select-popper"
                      :loading="loading"
                  >
                    <el-option
                        v-for="coach in coaches"
                        :key="coach.id"
                        :label="`${coach.realName} (${coach.coachNumber})`"
                        :value="coach.id"
                    >
                      <div class="coach-option">
                        <div class="coach-name">{{ coach.realName }}</div>
                        <div class="coach-details">
                          <span class="coach-number">{{ coach.coachNumber }}</span>
                          <el-divider direction="vertical"></el-divider>
                          <span class="coach-level">{{ getLevelText(coach.level) }}</span>
                        </div>
                      </div>
                    </el-option>
                  </el-select>
                </div>

                <!-- 日期选择 -->
                <div class="form-group">
                  <div class="form-label">选择日期</div>
                  <el-date-picker
                      v-model="reservationForm.date"
                      type="date"
                      placeholder="选择日期"
                      :picker-options="dateOptions"
                      @change="handleDateChange"
                      value-format="yyyy-MM-dd"
                      format="yyyy-MM-dd"
                      class="mobile-datepicker"
                      popper-class="mobile-datepicker-popper"
                      :disabled="!reservationForm.coachId"
                  ></el-date-picker>
                  <div class="date-hint">
                    <i class="el-icon-info hint-icon"></i>
                    可预约未来 {{ advanceDays }} 天内的日期
                  </div>
                </div>

                <!-- 时段选择 -->
                <div class="form-group time-slots-group">
                  <div class="form-header">
                    <div class="form-label">选择时段</div>
                    <div v-if="timeSlotsLoading" class="time-slots-loading">
                      <i class="el-icon-loading"></i>
                      <span>加载中...</span>
                    </div>
                    <span v-else-if="availableTimeSlots.length" class="time-slots-count">
                      {{ availableTimeSlots.filter(s => s.available && !s.timeInvalid && !s.isFull && !s.studentAlreadyReserved).length }} 个可预约
                    </span>
                  </div>

                  <!-- 时段骨架屏 -->
                  <div v-if="timeSlotsLoading" class="time-slots-skeleton">
                    <el-skeleton :rows="3" animated />
                  </div>

                  <div v-else-if="availableTimeSlots.length" class="time-slots-list">
                    <div
                        v-for="slot in availableTimeSlots"
                        :key="slot.value"
                        class="time-slot-item"
                        :class="getTimeSlotClass(slot)"
                        @click="selectTimeSlot(slot)"
                    >
                      <div class="slot-content">
                        <div class="slot-time">{{ slot.label }}</div>
                        <div class="slot-status">
                          <i v-if="slot.available && !slot.timeInvalid && !slot.isFull && !slot.studentAlreadyReserved"
                             class="el-icon-success available-icon"></i>
                          <i v-else class="el-icon-error unavailable-icon"></i>
                          <span class="status-text">{{ getSlotStatusText(slot) }}</span>
                        </div>
                      </div>

                      <!-- 人数容量显示 -->
                      <div v-if="slot.available && !slot.timeInvalid && slot.maxStudents && !slot.studentAlreadyReserved"
                           class="slot-capacity">
                        <div class="capacity-bar">
                          <div class="capacity-fill" :style="{width: slot.capacityPercent + '%'}"></div>
                        </div>
                        <div class="capacity-text">
                          {{ slot.currentStudents }}/{{ slot.maxStudents }}人
                        </div>
                      </div>

                      <div v-if="slot.reason || slot.timeInvalidReason || slot.studentAlreadyReserved"
                           class="slot-reason">
                        {{ slot.studentAlreadyReserved ? '您已预约该时段' : (slot.reason || slot.timeInvalidReason) }}
                      </div>
                    </div>
                  </div>

                  <div v-else class="no-slots-state">
                    <i class="el-icon-time no-slots-icon"></i>
                    <div class="no-slots-text">
                      <span v-if="reservationForm.coachId && reservationForm.date">
                        该日期暂无可用时段
                      </span>
                      <span v-else>
                        请先选择教练和日期
                      </span>
                    </div>
                  </div>
                </div>

                <!-- 训练科目 -->
                <div class="form-group">
                  <div class="form-label">训练科目</div>
                  <el-select
                      v-model="reservationForm.subject"
                      placeholder="选择训练科目"
                      class="mobile-select"
                      popper-class="mobile-select-popper"
                  >
                    <el-option label="科目二" value="SUBJECT2"></el-option>
                    <el-option label="科目三" value="SUBJECT3"></el-option>
                  </el-select>
                </div>

                <!-- 备注 -->
                <div class="form-group">
                  <div class="form-label">备注信息</div>
                  <el-input
                      type="textarea"
                      v-model="reservationForm.remark"
                      placeholder="请输入备注信息（可选）"
                      :rows="3"
                      class="mobile-textarea"
                      resize="none"
                      maxlength="100"
                      show-word-limit
                  ></el-input>
                </div>

                <!-- 预约限制提示 -->
                <div class="reservation-hint" :class="{ 'warning': isOverReservationLimit }">
                  <i class="el-icon-info hint-icon"></i>
                  <div class="hint-content">
                    <template v-if="isOverReservationLimit">
                      <strong>今日预约次数已用完</strong>（{{ todayReservationCount }}/{{ maxDailyAppointments }}次）
                    </template>
                    <template v-else>
                      <div class="hint-main">
                        每日最多可预约 {{ maxDailyAppointments }} 次
                      </div>
                      <div class="hint-sub">
                        需提前 {{ minAdvanceHours }} 小时预约
                        <span v-if="todayReservationCount > 0" class="today-count">
                          · 今日已预约 {{ todayReservationCount }} 次
                        </span>
                      </div>
                    </template>
                  </div>
                </div>

                <!-- 操作按钮 - 桌面端 -->
                <div v-if="!isMobile" class="form-actions">
                  <el-button
                      type="primary"
                      @click="submitReservation"
                      :loading="submitting"
                      :disabled="!canSubmitForm"
                      class="submit-btn"
                  >
                    <i class="el-icon-check"></i>
                    {{ isOverReservationLimit ? '预约次数已用完' : '提交预约' }}
                  </el-button>
                  <el-button
                      @click="resetForm"
                      class="reset-btn"
                  >
                    <i class="el-icon-refresh"></i>
                    重置表单
                  </el-button>
                </div>
              </el-form>
            </div>
          </el-card>
        </div>

        <!-- 桌面端：教练排班信息放在右侧 -->
        <CoachScheduleInfo
            v-if="!isMobile && coachScheduleInfo"
            :coach-schedule-info="coachScheduleInfo"
            :is-mobile="isMobile"
            :expanded="isScheduleExpanded"
            @toggle="toggleScheduleExpanded"
        />
      </div>

      <!-- 移动端底部固定按钮 -->
      <div v-if="isMobile" class="mobile-fixed-footer">
        <div class="mobile-actions">
          <el-button
              @click="resetForm"
              class="mobile-reset-btn"
              size="large"
          >
            <i class="el-icon-refresh"></i>
            重置
          </el-button>
          <el-button
              type="primary"
              @click="submitReservation"
              :loading="submitting"
              :disabled="!canSubmitForm"
              class="mobile-submit-btn"
              size="large"
          >
            <i class="el-icon-check"></i>
            {{ isOverReservationLimit ? '次数已用完' : '提交预约' }}
          </el-button>
        </div>
      </div>
    </div>
  </GlobalBackgroundWrapper>
</template>

<script>
import { coachAPI, reservationAPI, systemAPI } from '@/services/api'
import GlobalBackgroundWrapper from '@/components/GlobalBackgroundWrapper.vue'
import CoachScheduleInfo from '@/components/CoachScheduleInfo.vue'
import { validateReservationForm, formatError } from '@/utils/validation'

export default {
  name: 'ReserVation',
  components: {
    GlobalBackgroundWrapper,
    CoachScheduleInfo
  },
  data() {
    return {
      loading: false,
      submitting: false,
      timeSlotsLoading: false,
      coaches: [],
      availableTimeSlots: [],
      coachScheduleInfo: null,
      systemSettings: null,
      todayReservationCount: 0,
      isScheduleExpanded: false,
      reservationForm: {
        coachId: '',
        date: '',
        timeSlot: '',
        subject: 'SUBJECT2',
        remark: ''
      },
      allTimeSlots: [
        { value: '08:00-10:00', label: '08:00-10:00' },
        { value: '10:00-12:00', label: '10:00-12:00' },
        { value: '14:00-16:00', label: '14:00-16:00' },
        { value: '16:00-18:00', label: '16:00-18:00' },
        { value: '18:00-20:00', label: '18:00-20:00' }
      ],
      isMobile: false,
      myReservations: [] // 缓存学员的预约列表
    }
  },
  computed: {
    maxDailyAppointments() {
      return this.systemSettings?.maxDailyAppointments || 2
    },
    advanceDays() {
      return this.systemSettings?.advanceDays || 7
    },
    minAdvanceHours() {
      return this.systemSettings?.minAdvanceHours || 2
    },

    dateOptions() {
      return {
        disabledDate: (time) => {
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          const todayTime = today.getTime()

          const maxDate = new Date()
          maxDate.setDate(today.getDate() + this.advanceDays)
          maxDate.setHours(23, 59, 59, 999)
          const maxDateTime = maxDate.getTime()

          return time.getTime() < todayTime || time.getTime() > maxDateTime
        }
      }
    },

    isOverReservationLimit() {
      return this.todayReservationCount >= this.maxDailyAppointments
    },

    canSubmitForm() {
      return validateReservationForm(
          this.reservationForm,
          this.availableTimeSlots,
          {
            isOverReservationLimit: this.isOverReservationLimit,
            minAdvanceHours: this.minAdvanceHours
          }
      ).isValid
    }
  },
  async mounted() {
    this.checkIsMobile()
    window.addEventListener('resize', this.checkIsMobile)
    await this.initData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIsMobile)
  },
  methods: {
    async initData() {
      try {
        this.loading = true
        await Promise.all([
          this.loadSystemSettings(),
          this.loadCoaches(),
          this.loadMyReservations()
        ])
        await this.loadTodayReservationCount()
      } catch (error) {
        console.error('初始化数据失败:', error)
        this.showMessage('加载数据失败，请刷新页面重试', 'error')
      } finally {
        this.loading = false
      }
    },

    toggleScheduleExpanded() {
      this.isScheduleExpanded = !this.isScheduleExpanded
    },

    async loadMyReservations() {
      try {
        const response = await reservationAPI.getMyReservations()
        if (response && response.data) {
          this.myReservations = response.data
          console.log('加载我的预约列表成功:', this.myReservations.length, '条记录')
        } else {
          this.myReservations = []
        }
      } catch (error) {
        console.error('加载我的预约列表失败:', error)
        this.myReservations = []
      }
    },

    async loadTodayReservationCount() {
      const today = new Date().toISOString().split('T')[0]
      this.todayReservationCount = this.myReservations.filter(reservation =>
          reservation.date === today &&
          reservation.status !== 'CANCELLED' &&
          reservation.status !== 'REJECTED'
      ).length
      console.log('今日已预约次数:', this.todayReservationCount)
    },

    checkIsMobile() {
      this.isMobile = window.innerWidth <= 768
    },

    async loadSystemSettings() {
      try {
        const response = await systemAPI.getSettings()
        if (response && response.data) {
          this.systemSettings = response.data
          console.log('系统设置加载成功:', this.systemSettings)
        } else {
          this.systemSettings = {
            advanceDays: 7,
            minAdvanceHours: 2,
            maxDailyAppointments: 2,
            maxDuration: 4,
            freeCancelHours: 24
          }
        }
      } catch (error) {
        console.error('加载系统设置失败:', error)
        this.systemSettings = {
          advanceDays: 7,
          minAdvanceHours: 2,
          maxDailyAppointments: 2,
          maxDuration: 4,
          freeCancelHours: 24
        }
      }
    },

    async loadCoaches() {
      try {
        const response = await coachAPI.getCoaches()
        if (response && response.data) {
          this.coaches = response.data
          console.log('加载教练列表成功:', this.coaches.length, '位教练')
        } else {
          this.showMessage('获取教练数据失败', 'error')
        }
      } catch (error) {
        console.error('加载教练列表失败:', error)
        this.showMessage('加载教练列表失败: ' + (error.response?.data?.message || error.message), 'error')
      }
    },

    async handleCoachChange() {
      this.reservationForm.date = ''
      this.reservationForm.timeSlot = ''
      this.availableTimeSlots = []
      this.coachScheduleInfo = null

      if (this.reservationForm.coachId && this.reservationForm.date) {
        await this.loadTimeSlots()
      }
    },

    async handleDateChange() {
      if (this.reservationForm.coachId && this.reservationForm.date) {
        await this.loadTimeSlots()
      } else {
        this.availableTimeSlots = []
        this.coachScheduleInfo = null
      }
    },

    showMessage(message, type = 'info') {
      const position = this.isMobile ? 'top' : 'top-right'
      this.$message({
        message,
        type,
        position,
        duration: 3000,
        showClose: true
      })
    },

    showWarning(message) {
      const position = this.isMobile ? 'top' : 'top-right'
      this.$message({
        message,
        type: 'warning',
        position,
        duration: 3000,
        showClose: true
      })
    },

    getLevelText(level) {
      const levelMap = {
        'senior': '高级教练',
        'intermediate': '中级教练',
        'primary': '初级教练',
      }
      return levelMap[level] || level || '未知等级'
    },

    getTimeSlotClass(slot) {
      const classes = {
        'available': slot.available && !slot.timeInvalid && !slot.isFull && !slot.studentAlreadyReserved,
        'unavailable': !slot.available || slot.timeInvalid || slot.isFull,
        'selected': this.reservationForm.timeSlot === slot.value, // 修正：使用 this.reservationForm
        'time-invalid': slot.timeInvalid,
        'full': slot.isFull,
        'reserved': slot.studentAlreadyReserved
      }

      return Object.entries(classes)
          .filter(([, value]) => value)
          .map(([key]) => key)
          .join(' ')
    },

    getSlotStatusText(slot) {
      if (slot.studentAlreadyReserved) return '已预约'
      if (slot.timeInvalid) return '时间不符'
      if (slot.isFull) return '已满员'
      if (slot.available) return '可预约'
      return '不可预约'
    },

    checkTimeSlotValidity(date, timeSlot) {
      const startTimeStr = timeSlot.split('-')[0]
      const reservationDateTime = new Date(`${date} ${startTimeStr}`)
      const now = new Date()

      const hoursDiff = (reservationDateTime - now) / (1000 * 60 * 60)
      return hoursDiff >= this.minAdvanceHours
    },

    async loadTimeSlots() {
      if (!this.reservationForm.coachId || !this.reservationForm.date) {
        this.availableTimeSlots = []
        this.coachScheduleInfo = null
        return
      }

      this.timeSlotsLoading = true
      try {
        // 1. 获取教练在该日期的排班信息
        const scheduleResponse = await coachAPI.getCoachSchedule({
          coachId: this.reservationForm.coachId,
          date: this.reservationForm.date
        })

        console.log('教练排班信息:', scheduleResponse)

        if (scheduleResponse && scheduleResponse.data) {
          const schedule = scheduleResponse.data
          const coach = this.coaches.find(c => c.id === this.reservationForm.coachId)

          // 设置教练排班信息
          this.coachScheduleInfo = {
            coachName: coach ? `${coach.realName} (${coach.coachNumber})` : '未知教练',
            date: this.reservationForm.date,
            maxStudents: schedule.maxStudents || 1,
            timeSlots: schedule.timeSlots || [],
            remark: schedule.remark || ''
          }

          // 2. 使用现有的单时段查询方法逐个获取时段状态
          this.availableTimeSlots = await Promise.all(
              this.allTimeSlots.map(async (slot) => {
                try {
                  // 使用单时段查询方法
                  const statusResponse = await reservationAPI.getTimeSlotStatus({
                    coachId: this.reservationForm.coachId,
                    date: this.reservationForm.date,
                    timeSlot: slot.value
                  })

                  const status = statusResponse.data
                  console.log(`时间段 ${slot.value} 状态:`, status)

                  const isScheduled = schedule.timeSlots && schedule.timeSlots.includes(slot.value)
                  const isTimeValid = this.checkTimeSlotValidity(this.reservationForm.date, slot.value)

                  // 检查当前学员是否已预约该时段
                  const studentAlreadyReserved = this.myReservations.some(reservation =>
                      reservation.coachId === this.reservationForm.coachId &&
                      reservation.date === this.reservationForm.date &&
                      reservation.timeSlot === slot.value &&
                      reservation.status !== 'CANCELLED' &&
                      reservation.status !== 'REJECTED'
                  )

                  console.log(`时段 ${slot.value} 学员是否已预约:`, studentAlreadyReserved)

                  let available = false
                  if (!studentAlreadyReserved && isScheduled && status.available && isTimeValid) {
                    available = true
                  }

                  // 是否已满员
                  const isFull = status.currentStudents >= status.maxStudents

                  let reason = ''
                  if (studentAlreadyReserved) {
                    reason = '您已预约该时段'
                  } else if (!isScheduled) {
                    reason = '教练未排班'
                  } else if (isFull) {
                    reason = `已满员（${status.currentStudents}/${status.maxStudents}人）`
                  } else if (!isTimeValid) {
                    reason = `请至少提前${this.minAdvanceHours}小时预约`
                  } else if (!status.available) {
                    reason = status.reason || '不可预约'
                  }

                  return {
                    ...slot,
                    available: available,
                    timeInvalid: !isTimeValid,
                    isFull: isFull,
                    studentAlreadyReserved: studentAlreadyReserved,
                    currentStudents: status.currentStudents || 0,
                    maxStudents: status.maxStudents || 1,
                    capacityPercent: Math.min(((status.currentStudents || 0) / (status.maxStudents || 1)) * 100, 100),
                    reason: reason,
                    timeInvalidReason: !isTimeValid ? `请至少提前${this.minAdvanceHours}小时预约` : ''
                  }

                } catch (error) {
                  console.error(`获取时间段 ${slot.value} 状态失败:`, error)
                  return {
                    ...slot,
                    available: false,
                    timeInvalid: false,
                    isFull: false,
                    studentAlreadyReserved: false,
                    currentStudents: 0,
                    maxStudents: 1,
                    capacityPercent: 0,
                    reason: '获取状态失败'
                  }
                }
              })
          )

          console.log('生成的可用时段:', this.availableTimeSlots)

        } else {
          // 没有排班信息
          this.coachScheduleInfo = {
            coachName: this.coaches.find(c => c.id === this.reservationForm.coachId)?.realName || '未知教练',
            date: this.reservationForm.date,
            maxStudents: 1,
            timeSlots: [],
            remark: ''
          }
          this.availableTimeSlots = this.allTimeSlots.map(slot => ({
            ...slot,
            available: false,
            timeInvalid: false,
            isFull: false,
            studentAlreadyReserved: false,
            currentStudents: 0,
            maxStudents: 1,
            capacityPercent: 0,
            reason: '教练未排班'
          }))
        }

        // 重置已选择的时间段
        this.reservationForm.timeSlot = ''

      } catch (error) {
        console.error('加载可用时段失败:', error)
        this.showMessage('加载可用时段失败: ' + (error.response?.data?.message || error.message), 'error')
        this.availableTimeSlots = []
        this.coachScheduleInfo = null
      } finally {
        this.timeSlotsLoading = false
      }
    },

    selectTimeSlot(slot) {
      console.log('选择时间段:', slot.label, '学员已预约:', slot.studentAlreadyReserved)

      // 优先判断：已预约则直接提示并返回，彻底禁止交互
      if (slot.studentAlreadyReserved) {
        this.showWarning('您已预约该时段，不可重复预约')
        return
      }

      // 其他判断逻辑
      if (slot.available && !slot.timeInvalid && !slot.isFull) {
        this.reservationForm.timeSlot = slot.value
      } else {
        let message = slot.reason || `该时段不可预约`
        if (slot.timeInvalid) {
          message = slot.timeInvalidReason
        } else if (slot.isFull) {
          message = slot.reason
        }
        this.showWarning(message)
      }
    },

    async submitReservation() {
      // 在提交前强制刷新我的预约数据，确保检查最新状态
      try {
        await this.loadMyReservations()
        await this.loadTodayReservationCount()
      } catch (error) {
        console.error('刷新预约数据失败:', error)
        this.showWarning('获取最新预约数据失败，请刷新页面后重试')
        return
      }

      // 提交前再次检查是否已预约该时段（双重保障）
      const isAlreadyReserved = this.myReservations.some(reservation => {
        const reservationCoachId = Number(reservation.coachId)
        const currentCoachId = Number(this.reservationForm.coachId)

        return reservationCoachId === currentCoachId &&
            reservation.date === this.reservationForm.date &&
            reservation.timeSlot === this.reservationForm.timeSlot &&
            reservation.status !== 'CANCELLED' &&
            reservation.status !== 'REJECTED'
      })

      if (isAlreadyReserved) {
        this.showWarning('您已预约该时段，请勿重复提交')
        // 刷新时段状态
        await this.loadTimeSlots()
        return
      }

      // 验证表单
      const validation = validateReservationForm(
          this.reservationForm,
          this.availableTimeSlots,
          {
            isOverReservationLimit: this.isOverReservationLimit,
            minAdvanceHours: this.minAdvanceHours
          }
      )

      if (!validation.isValid) {
        this.showWarning(validation.errors[0])
        return
      }

      // 额外的时间有效性检查
      if (!this.checkTimeSlotValidity(this.reservationForm.date, this.reservationForm.timeSlot)) {
        this.showWarning(`请至少提前${this.minAdvanceHours}小时预约`)
        return
      }

      this.submitting = true
      try {
        const reservationData = {
          coachId: Number(this.reservationForm.coachId),
          date: this.reservationForm.date,
          timeSlot: this.reservationForm.timeSlot,
          subject: this.reservationForm.subject,
          remark: this.reservationForm.remark
        }

        console.log('提交预约数据:', reservationData)

        // 添加请求超时控制
        const response = await Promise.race([
          reservationAPI.create(reservationData),
          new Promise((_, reject) =>
              setTimeout(() => reject(new Error('请求超时，请稍后重试')), 10000)
          )
        ])

        console.log('API响应:', response)

        if (response && response.code === 200) {
          // 显示简短的成功消息
          this.showMessage('预约成功！正在更新数据...', 'success')

          // 保存当前选择（如果需要）
          const selectedCoachId = this.reservationForm.coachId
          const selectedDate = this.reservationForm.date

          // 重置表单
          this.resetForm()

          // 显示加载状态提示
          const loadingMessage = this.$message({
            message: '正在更新页面数据...',
            type: 'info',
            duration: 0, // 不自动关闭
            showClose: false
          })

          try {
            // 重新初始化所有数据
            await this.initData()

            // 如果之前有选择教练和日期，重新加载该教练和日期的时段
            if (selectedCoachId && selectedDate) {
              this.reservationForm.coachId = selectedCoachId
              this.reservationForm.date = selectedDate
              await this.loadTimeSlots()
            }

            // 关闭加载消息
            loadingMessage.close()

            // 显示最终成功消息
            this.$message({
              message: '预约成功！页面数据已更新',
              type: 'success',
              duration: 3000,
              showClose: true
            })

          } catch (refreshError) {
            // 关闭加载消息
            loadingMessage.close()

            console.error('刷新数据失败:', refreshError)
            this.$message({
              message: '预约成功，但更新页面数据时遇到问题，建议手动刷新页面',
              type: 'warning',
              duration: 5000,
              showClose: true
            })
          }
        } else {
          this.showMessage(response?.message || '预约失败', 'error')
        }
      } catch (error) {
        // 处理 CORS 相关的网络错误
        if (error.message.includes('网络连接失败') && !error.response) {
          this.showWarning('您已预约该时段，请勿重复提交')

          // 如果出现CORS错误，我们仍然需要更新本地数据，以防后端实际处理了请求
          setTimeout(async () => {
            try {
              await this.loadMyReservations()
              await this.loadTodayReservationCount()
              await this.loadTimeSlots()
            } catch (refreshError) {
              console.error('CORS错误后刷新数据失败:', refreshError)
            }
          }, 1000)

          return
        }

        // 处理其他类型的错误
        const { message, type, action } = formatError(error, {
          minAdvanceHours: this.minAdvanceHours
        })

        // 显示消息
        if (type === 'warning') {
          this.showWarning(message)
        } else {
          this.showMessage(message, 'error')
        }

        // 执行相应的操作
        try {
          if (action === 'refresh') {
            await this.loadTimeSlots()
          } else if (action === 'updateCount') {
            await this.loadMyReservations()
            await this.loadTodayReservationCount()
          } else if (action === 'all') {
            // 刷新所有数据
            await this.loadMyReservations()
            await this.loadTodayReservationCount()
            await this.loadTimeSlots()
          }
        } catch (refreshError) {
          console.error('执行错误处理操作失败:', refreshError)
        }
      } finally {
        this.submitting = false
      }
    },

    resetForm() {
      this.reservationForm = {
        coachId: '',
        date: '',
        timeSlot: '',
        subject: 'SUBJECT2',
        remark: ''
      }
      this.availableTimeSlots = []
      this.coachScheduleInfo = null
      this.isScheduleExpanded = false
    }
  }
}
</script>

<style scoped>
/* 基础样式 */
.reservation-container {
  padding: 16px;
  max-width: 100%;
  margin: 0 auto;
  min-height: 100vh;
  padding-bottom: 80px; /* 为底部按钮留出空间 */
  position: relative;
  z-index: 1;
}

/* 页面头部 */
.reservation-header {
  margin-bottom: 16px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.header-icon {
  font-size: 24px;
  color: #409EFF;
  padding: 8px;
  background: #ecf5ff;
  border-radius: 50%;
}

.header-text {
  flex: 1;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.page-subtitle {
  font-size: 14px;
  color: #606266;
  margin: 0;
}

/* 警告横幅 */
.reservation-warning-banner {
  background: linear-gradient(135deg, rgba(253, 246, 236, 0.9), rgba(250, 236, 216, 0.9));
  border: 1px solid rgba(245, 218, 177, 0.5);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
}

.warning-banner-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.warning-banner-icon {
  font-size: 20px;
  color: #e6a23c;
  flex-shrink: 0;
  margin-top: 2px;
}

.warning-banner-text {
  flex: 1;
}

.warning-title {
  font-size: 16px;
  font-weight: 600;
  color: #e6a23c;
  margin-bottom: 4px;
}

.warning-desc {
  font-size: 14px;
  line-height: 1.4;
  color: #e6a23c;
}

/* 主要内容布局 */
.reservation-content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

/* 移动端：教练排班信息放在前面 */
.coach-schedule-section.mobile-first {
  margin-bottom: 16px;
}

.coach-schedule-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.schedule-header-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.schedule-header-content .header-icon {
  font-size: 20px;
  color: #67c23a;
  padding: 6px;
  background: #f0f9eb;
}

.schedule-header-content .header-text {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.remark-tip {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #e6a23c;
  padding: 4px 8px;
  background: rgba(253, 246, 236, 0.8);
  border-radius: 4px;
  margin-left: auto;
}

.tip-icon {
  font-size: 14px;
}

.main-form-section {
  width: 100%;
}

/* 卡片样式 */
.form-card {
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.form-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
}

.card-header {
  padding: 16px;
  border-bottom: 1px solid rgba(235, 238, 245, 0.5);
  background: rgba(250, 251, 252, 0.0);
}

.card-header .header-content {
  padding: 0;
  background: none;
  box-shadow: none;
  gap: 8px;
  backdrop-filter: none;
}

.card-header .header-icon {
  font-size: 18px;
  padding: 6px;
}

.card-header .header-text {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

/* 骨架屏 */
.form-skeleton {
  padding: 24px;
}

.time-slots-skeleton {
  padding: 16px;
}

.time-slots-loading {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #909399;
}

.time-slots-loading i {
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 表单内容 */
.form-content {
  padding: 16px;
}

.reservation-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 表单项组 */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.form-label {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

/* 移动端表单控件 */
.mobile-select ::v-deep .el-input__inner,
.mobile-datepicker ::v-deep .el-input__inner {
  height: 44px;
  border-radius: 8px;
  color: black;
  border: 1px solid rgba(220, 223, 230, 0.5);
  font-size: 16px;
  background: rgba(255, 255, 255, 0.6);
}

.mobile-textarea ::v-deep .el-textarea__inner {
  border-radius: 8px;
  border: 1px solid rgba(220, 223, 230, 0.5);
  font-size: 16px;
  line-height: 1.5;
  padding: 12px;
  background: rgba(255, 255, 255, 0.9);
}

/* 日期提示 */
.date-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #000000;
  margin-top: 4px;
}

.hint-icon {
  font-size: 14px;
}

/* 时段选择 */
.time-slots-group {
  margin-top: 8px;
}

.time-slots-count {
  font-size: 14px;
  color: #409EFF;
  font-weight: 500;
}

.time-slots-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.time-slot-item {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(228, 231, 237, 0.5);
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
}

/* 强化已预约时段样式，提高优先级，禁止所有鼠标交互 */
.time-slot-item.reserved {
  border-color: #dcdfe6 !important;
  background: #f5f7fa !important;
  cursor: not-allowed !important;
  opacity: 0.7 !important;
  pointer-events: none !important;
}

.time-slot-item.available {
  border-color: rgba(225, 243, 216, 0.7);
  background: rgba(240, 249, 235, 0.9);
}

.time-slot-item.selected {
  border-color: rgba(64, 158, 255, 0.7);
  background: rgba(236, 245, 255, 0.9);
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
}

.time-slot-item.unavailable,
.time-slot-item.time-invalid {
  border-color: rgba(253, 226, 226, 0.7);
  background: rgba(254, 240, 240, 0.9);
  cursor: not-allowed;
  opacity: 0.7;
}

.time-slot-item.full {
  border-color: rgba(253, 226, 226, 0.7);
  background: rgba(254, 240, 240, 0.9);
  cursor: not-allowed;
  opacity: 0.7;
}

.slot-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.slot-time {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.slot-status {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
}

.available-icon {
  color: #67c23a;
  font-size: 16px;
}

.unavailable-icon {
  color: #f56c6c;
  font-size: 16px;
}

.status-text {
  font-weight: 500;
}

.time-slot-item.available .status-text {
  color: #67c23a;
}

.time-slot-item.unavailable .status-text,
.time-slot-item.time-invalid .status-text,
.time-slot-item.full .status-text {
  color: #f56c6c;
}

.slot-reason {
  font-size: 12px;
  color: #f56c6c;
  margin-top: 4px;
}

/* 容量条 */
.slot-capacity {
  margin-top: 8px;
}

.capacity-bar {
  height: 6px;
  background: rgba(220, 223, 230, 0.5);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 4px;
}

.capacity-fill {
  height: 100%;
  background: linear-gradient(90deg, #67c23a, #5daf34);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.capacity-text {
  font-size: 12px;
  color: #606266;
  text-align: center;
}

/* 无时段状态 */
.no-slots-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  color: #909399;
  text-align: center;
}

.no-slots-icon {
  font-size: 32px;
  color: #c0c4cc;
}

.no-slots-text {
  font-size: 14px;
}

/* 预约提示 */
.reservation-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  background: rgba(248, 249, 250, 0.0);
  border-radius: 6px;
  border-left: 3px solid #409EFF;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
}

.reservation-hint.warning {
  background: rgba(253, 246, 236, 0.7);
  border-left-color: #e6a23c;
}

.reservation-hint .hint-icon {
  font-size: 16px;
  color: #409EFF;
  margin-top: 2px;
}

.reservation-hint.warning .hint-icon {
  color: #e6a23c;
}

.hint-content {
  flex: 1;
}

.hint-main {
  font-size: 14px;
  color: #606266;
  margin-bottom: 2px;
}

.hint-sub {
  font-size: 13px;
  color: #909399;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.today-count {
  color: #409eff;
}

/* 操作按钮 */
.form-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  padding-top: 16px;
  border-top: 1px solid rgba(235, 238, 245, 0.5);
}

.submit-btn, .reset-btn {
  flex: 1;
  height: 44px;
  font-size: 16px;
  border-radius: 8px;
}

/* 移动端底部按钮 */
.mobile-fixed-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.mobile-actions {
  display: flex;
  gap: 12px;
}

.mobile-reset-btn,
.mobile-submit-btn {
  flex: 1;
  height: 48px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
}

.mobile-submit-btn {
  background: #409EFF;
  border: none;
}

.mobile-submit-btn:disabled {
  background: rgba(192, 196, 204, 0.7);
  cursor: not-allowed;
}

/* 桌面端样式 */
@media (min-width: 769px) {
  .reservation-container {
    padding: 24px;
    max-width: 1200px;
    padding-bottom: 24px;
  }

  .reservation-content {
    grid-template-columns: 2fr 1fr;
    gap: 24px;
  }

  /* 在桌面端隐藏移动端教练排班信息头部 */
  .coach-schedule-section.mobile-first {
    display: none;
  }

  .header-content {
    padding: 20px;
    gap: 16px;
  }

  .header-icon {
    font-size: 32px;
    padding: 12px;
  }

  .page-title {
    font-size: 24px;
  }

  .page-subtitle {
    font-size: 16px;
  }

  .form-content {
    padding: 24px;
  }

  .reservation-form {
    gap: 24px;
  }

  .form-label {
    font-size: 14px;
  }

  /* 隐藏移动端底部按钮 */
  .mobile-fixed-footer {
    display: none;
  }
}

/* 超小屏幕优化 */
@media (max-width: 480px) {
  .reservation-container {
    padding: 12px;
    padding-bottom: 80px;
  }

  .header-content {
    padding: 12px;
  }

  .coach-schedule-header {
    padding: 10px;
  }

  .page-title {
    font-size: 18px;
  }

  .page-subtitle {
    font-size: 13px;
  }

  .remark-tip {
    font-size: 12px;
    padding: 3px 6px;
  }

  .form-content {
    padding: 12px;
  }

  .reservation-form {
    gap: 16px;
  }

  .time-slot-item {
    padding: 10px;
  }

  .slot-time {
    font-size: 15px;
  }

  .slot-status {
    font-size: 13px;
  }

  .mobile-actions {
    gap: 8px;
  }

  .mobile-reset-btn,
  .mobile-submit-btn {
    height: 44px;
    font-size: 15px;
  }
}

/* 全局样式覆盖 */
::v-deep .el-card__header {
  padding: 16px;
}

::v-deep .el-card__body {
  padding: 0;
}

::v-deep .el-form-item {
  margin-bottom: 0;
}

/* 移动端选择器优化 */
::v-deep .mobile-select-popper,
::v-deep .mobile-datepicker-popper {
  width: 90vw !important;
  left: 5vw !important;
  max-height: 70vh !important;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

::v-deep .mobile-select-popper .el-select-dropdown__list,
::v-deep .mobile-datepicker-popper .el-picker-panel {
  max-height: 60vh !important;
  background: rgba(255, 255, 255, 0.95);
}

::v-deep .el-select-dropdown__item {
  height: 44px !important;
  line-height: 44px !important;
  font-size: 16px !important;
}

.coach-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 8px 0;
}

.coach-name {
  font-weight: 500;
  color: #303133;
}

.coach-details {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #909399;
}
</style>

<style>
/* 全局样式 - 移动端确认对话框优化 */
.mobile-confirm-dialog {
  width: 90% !important;
  max-width: 320px !important;
  margin: 0 auto !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
}

.mobile-confirm-dialog .el-message-box__content {
  padding: 15px 20px !important;
}

.mobile-confirm-dialog .el-message-box__btns {
  padding: 10px 20px 15px !important;
}

/* 移动端消息提示位置优化 */
@media (max-width: 768px) {
  .el-message {
    min-width: 90% !important;
    width: auto !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.95) !important;
  }
}

/* 桌面端消息提示位置优化 */
@media (min-width: 769px) {
  .el-message {
    min-width: 380px !important;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.95) !important;
  }
}

/* 背景设置抽屉样式 */
.background-settings-drawer {
  width: 320px !important;
}

@media (max-width: 768px) {
  .background-settings-drawer {
    width: 85% !important;
  }
}
</style>
